import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import LogRocket from 'logrocket';
import _get from 'lodash/get';
import _omit from 'lodash/omit';

import { AuthContext } from './AuthContext';
import useStore from 'utils/hooks/useStore';
import useUserMetaData from 'utils/hooks/useUserMetaData';
import isInternalUser from 'utils/methods/isInternalUser';
import {
  requestSanitizer,
  responseSanitizer,
} from 'utils/logRocket/sanitizers';

const RR_API_KEY = process.env.REACT_APP_RR_LOG_ROCKET_APP_ID;
const SSR_API_KEY = process.env.REACT_APP_SSR_LOG_ROCKET_APP_ID;
const IS_PROD = process.env.REACT_APP_MODE === 'PRODUCTION';

// Shopper PII kept out of LogRocket identify/track traits. Sessions remain
// searchable by user_id / customer_id.
const PII_TRAITS = ['email', 'name', 'first_name', 'last_name'];

/**
 * @type {import('react').Context<{
 *   track: (eventName: string, eventProperties?: Record<string, any>) => void;
 *   getSessionURL: () => string | null;
 * }>}
 */
export const LogRocketContext = React.createContext();

export function LogRocketProvider({ children = null }) {
  const { userData } = useContext(AuthContext);
  const { getStoreProperty } = useStore();
  const fullUserMetaData = useUserMetaData();
  const userMetaData = useMemo(
    () => _omit(fullUserMetaData, PII_TRAITS),
    [fullUserMetaData],
  );

  const userId = _get(userData, 'userId');
  const email = _get(userData, 'email');
  const isSSR = getStoreProperty('is_ssr_retailer', false);

  const apiKey = isSSR ? SSR_API_KEY : RR_API_KEY;
  const isTrackable = apiKey && IS_PROD && !isInternalUser(email);

  useEffect(() => {
    if (!isTrackable) return;

    try {
      LogRocket.init(apiKey, {
        console: { shouldAggregateConsoleErrors: true },
        // Never record the bearer token (Authorization header, login
        // response accessToken) or API payloads, which carry shopper PII.
        network: { requestSanitizer, responseSanitizer },
        // Mask all typed input and rendered text: login fields, addresses,
        // order, refund and gift-card details. Replays keep layout and
        // interactions only.
        dom: { inputSanitizer: true, textSanitizer: true },
        release: `${process.env.REACT_APP_VERSION}-${process.env.REACT_APP_CURRENT_GIT_SHA}`,
      });
      LogRocket.getSessionURL((sessionURL) => {
        Sentry.getCurrentScope().setExtra('sessionURL', sessionURL);
      });
    } catch (error) {
      console.error('Failed to initialize LogRocket:', error);
    }
  }, [isTrackable, apiKey]);

  useEffect(() => {
    if (!isTrackable || !userId) return;

    try {
      LogRocket.identify(userId, userMetaData);
    } catch (error) {
      console.error('Failed to identify user in LogRocket:', error);
    }
  }, [isTrackable, userId, userMetaData]);

  const track = useCallback(
    (eventName = '', eventProperties = {}) => {
      if (!isTrackable || !eventName) return;

      try {
        LogRocket.track(eventName, {
          ...userMetaData,
          ...eventProperties,
        });
      } catch (error) {
        console.error('Failed to track event in LogRocket:', error);
      }
    },
    [isTrackable, userMetaData],
  );

  const getSessionURL = useCallback(() => {
    if (!isTrackable) return null;

    // LogRocket returns the placeholder string "Must call LogRocket.init()
    // before sessionURL becomes available." instead of null when the
    // recording has not started, so only accept a real URL.
    const sessionURL = LogRocket.sessionURL;

    return typeof sessionURL === 'string' && sessionURL.startsWith('http')
      ? sessionURL
      : null;
  }, [isTrackable]);

  const value = useMemo(
    () => ({
      track,
      // Null when LogRocket never initialised or the recording has not
      // started yet, so callers must handle null.
      getSessionURL,
    }),
    [track, getSessionURL],
  );

  return (
    <LogRocketContext.Provider value={value}>
      {children}
    </LogRocketContext.Provider>
  );
}
